import React, { useEffect } from "react"
import { LogGaEvent } from "lib/GoogleAnalytics"
import { EventAction, EventCategory, EventLabel } from "models/GoogleAnalytics"

import MojoLogo from "url:assets/images/mojo-logo.png"
import ConveyancingIllustration from "url:assets/images/offer-accepted-status/conveyancing.png"
import { useHistory } from "react-router-dom"
import { ButtonType } from "components/atoms"
import { SectionCardComponent } from "./components/SectionCard"
import { Key1 } from "components/Illustrations"
import { useApplicationSummaryEnhanced } from "hooks/useApplicationSummaryEnhanced"
import {
  ContentContainer,
  Disclaimer,
  Footer,
  FooterLogo,
  FooterText,
  HeroContent,
  HeroSection,
  HeroText,
  HeroTitle,
  MainContent,
} from "./shared/StyledComponents"
import { SectionCard, SectionContent, SectionText, SectionTitle } from "./components/Card.styles"
import { MortgageExpertCardComponent } from "./components/MortgageExpertCard"
import { StatusData } from "./utils/statusApiUtils"
import MojoMiniLogo from "url:assets/images/mojo-mini-logo-alt.svg"

interface Props {
  friendlyId: string
  fetchedStatus?: StatusData | null
}

export const PaymentStatusPage: React.FC<Props> = ({ friendlyId, fetchedStatus }) => {
  const history = useHistory()
  const { application } = useApplicationSummaryEnhanced({ friendlyId })

  useEffect(() => {
    LogGaEvent({
      action: EventAction.componentViewed,
      event_category: EventCategory.siteInteraction,
      event_label: EventLabel.completeYourPayment,
    })
  }, [])

  const mortgageExpert = {
    name: fetchedStatus?.opportunityOwnerTextForEmail || "Mojo Mortgages",
    title: fetchedStatus?.flexRoles2 || "Mortgage Expert",
    email: fetchedStatus?.ownerEmailAddress || "",
    avatar: MojoMiniLogo,
  }

  const handleLearnMore = (section: string, uri: string) => {
    LogGaEvent({
      action: EventAction.buttonClicked,
      event_category: EventCategory.functionalInteraction,
      event_label: `Learn More - ${section}`,
    })
    history.push(uri)
  }

  const handlePayment = () => {
    LogGaEvent({
      action: EventAction.buttonClicked,
      event_category: EventCategory.functionalInteraction,
      event_label: "Pay Now - Specialist Fee",
    })
    if (application?.service?.checkoutSessionUrl) {
      window.location.href = application.service.checkoutSessionUrl
    }
  }

  return (
    <ContentContainer>
      <MainContent>
        <HeroSection>
          <HeroContent>
            <HeroTitle>Your buy-to-let mortgage application is underway</HeroTitle>
            <HeroText>
              Your Mortgage Expert {mortgageExpert?.name}, is now reviewing your documents and
              packaging your application. We're working to ensure it meets all the lender's
              requirements for a smooth approval.
            </HeroText>
            <HeroText>
              If you haven't already, please upload all requested documents via your secure link to
              avoid any delays.
            </HeroText>
          </HeroContent>
        </HeroSection>

        <SectionCardComponent
          title="Complete your payment"
          paragraphs={[
            "Before we can submit your application, the £499 application fee for your Mojo Specialist service needs to be paid. This fee covers the expert management of your BTL application. This fee is non-refundable.",
            "You can complete this step now to ensure a speedy submission.",
          ]}
          buttonText="Pay now"
          buttonType={ButtonType.Solid}
          onButtonClick={handlePayment}
          imageSrc={Key1}
          imageAlt="Family carrying a giant key"
          pillText="Action required"
        />

        <SectionCardComponent
          title="Secure your property's legal work"
          paragraphs={[
            "Conveyancing is a vital part of securing your property. It's important to instruct your chosen solicitor as soon as you're ready.",
            "Your Mortgage Expert has provided a number of quotes for you to compare and review.",
          ]}
          buttonText="View conveyancing quotes"
          buttonType={ButtonType.Outline}
          onButtonClick={() =>
            handleLearnMore("Conveyancing", `/application/${friendlyId}/conveyancing`)
          }
          imageSrc={ConveyancingIllustration}
          imageAlt="Legal conveyancing illustration"
        />
        <SectionCard columns={1}>
          <SectionContent>
            <SectionTitle>Your Mortgage Expert</SectionTitle>
            <SectionText>
              {mortgageExpert?.name} is on hand to help you finalise everything for your
              application.
            </SectionText>
            <MortgageExpertCardComponent mortgageExpert={mortgageExpert} />
          </SectionContent>
        </SectionCard>

        <Disclaimer>
          *Data taken using Mojo’s internal records, covering the period from 18 February 2025 to 30
          September 2025. Average savings over the initial term based on the difference between the
          customer’s initial pay rate and their new, lower rate discovered through our Rate Check
          Promise service.*
        </Disclaimer>
        <Footer>
          <FooterLogo src={MojoLogo} alt="Mojo" />
          <FooterText>
            Mojo is a trading style of Life's Great Limited which is registered in England and Wales
            (06246376). We are authorised and regulated by the Financial Conduct Authority and are
            on the Financial Services Register (478215) Our registered office is The Cooperage, 5
            Copper Row, London, SE1 2LH, and head office is Mojo Mortgages, WeWork No. 1
            Spinningfields, Quay Street, Manchester, M3 3JE.
          </FooterText>
        </Footer>
      </MainContent>
    </ContentContainer>
  )
}
